import React, { useState, useEffect, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  updateLoanRequest,
  fetchLoanRequestDetails,
  submitLoanRequest,
  clearLoanRequestsError,
} from "../../../../contents/redux/actions/loanRequestActions";
import {
  MonetizationOnRounded,
  LiveHelpRounded,
  PaymentsRounded,
  PercentRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import styles from "../../../utilities/cssmodules/EditLoan.module.css";

const EditLoan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPending, startTransition] = useTransition();
  const { requestId, requestTypeId } = location.state || {};
  const { loanDetails, isLoading, errors } = useSelector(
    (state) =>
      state.loanRequests || { loanDetails: null, isLoading: false, errors: {} },
  );
  const userId = localStorage.getItem("userId");
  const [inputs, setInputs] = useState({
    purpose: "",
    amount: "",
    currency: "",
    monthlyRepaymentRate: "",
  });
  const [formErrors, setFormErrors] = useState({
    purpose: "",
    amount: "",
    currency: "",
    monthlyRepaymentRate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const monthlyRepaymentRates = [
    { id: "1", name: "10" },
    { id: "2", name: "22" },
    { id: "3", name: "33" },
  ];

  const currencies = [
    { id: "1", name: "USD" },
    { id: "2", name: "LRD" },
  ];

  // Fetch request details
  useEffect(() => {
    if (!requestId) {
      console.warn("Missing requestId:", { requestId });
      toast.error("Invalid request ID");
      navigate("/myrequests");
      return;
    }
    startTransition(() => {
      dispatch(fetchLoanRequestDetails(requestId));
    });
  }, [requestId, dispatch, navigate]);

  // Restrict editing to saved requests
  useEffect(() => {
    if (loanDetails) {
      console.log("Checking status:", loanDetails.status);
      if (!loanDetails.status) {
        toast.error("Request status is undefined. Contact support.");
        navigate("/myrequests");
      } else if (loanDetails.status !== "Saved") {
        toast.error("Only saved requests can be edited.");
        navigate("/myrequests");
      }
    }
  }, [loanDetails, navigate]);

  // Populate form with fetched data
  useEffect(() => {
    if (loanDetails && loanDetails.id === requestId) {
      console.log("Populating form with loanDetails:", loanDetails);
      setInputs({
        purpose: loanDetails.purpose || "",
        amount: loanDetails.amount?.toString() || "",
        currency: loanDetails.currency || "",
        monthlyRepaymentRate:
          loanDetails.monthlyRepaymentRate?.toString() || "",
      });
    }
  }, [loanDetails, requestId]);

  // Handle Redux errors
  useEffect(() => {
    if (errors.fetchLoanRequestDetails) {
      toast.error(errors.fetchLoanRequestDetails);
      dispatch(clearLoanRequestsError());
    }
    if (errors.updateLoanRequest) {
      toast.error(errors.updateLoanRequest);
      dispatch(clearLoanRequestsError());
      setIsSubmitting(false);
    }
  }, [errors.fetchLoanRequestDetails, errors.updateLoanRequest, dispatch]);

  // Validate form inputs for isFormValid
  useEffect(() => {
    const newErrors = {
      purpose: "",
      amount: "",
      currency: "",
      monthlyRepaymentRate: "",
    };
    let isValid = true;

    if (!inputs.purpose.trim()) {
      newErrors.purpose = "Purpose is required";
      isValid = false;
    }

    const amountValue = parseFloat(inputs.amount);
    if (!inputs.amount || isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = "Please enter a valid positive amount";
      isValid = false;
    }

    if (!inputs.currency) {
      newErrors.currency = "Please select a currency";
      isValid = false;
    }

    const repaymentValue = parseFloat(inputs.monthlyRepaymentRate);
    if (
      !inputs.monthlyRepaymentRate ||
      isNaN(repaymentValue) ||
      repaymentValue <= 0
    ) {
      newErrors.monthlyRepaymentRate =
        "Please select a valid repayment percentage";
      isValid = false;
    }

    setIsFormValid(isValid);
    // Only update formErrors on save/submit, not here
  }, [inputs]);

  // Validate form inputs for save/submit
  const validateInputs = () => {
    const newErrors = {
      purpose: "",
      amount: "",
      currency: "",
      monthlyRepaymentRate: "",
    };
    let isValid = true;

    if (!inputs.purpose.trim()) {
      newErrors.purpose = "Purpose is required";
      isValid = false;
    }

    const amountValue = parseFloat(inputs.amount);
    if (!inputs.amount || isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = "Please enter a valid positive amount";
      isValid = false;
    }

    if (!inputs.currency) {
      newErrors.currency = "Please select a currency";
      isValid = false;
    }

    const repaymentValue = parseFloat(inputs.monthlyRepaymentRate);
    if (
      !inputs.monthlyRepaymentRate ||
      isNaN(repaymentValue) ||
      repaymentValue <= 0
    ) {
      newErrors.monthlyRepaymentRate =
        "Please select a valid repayment percentage";
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({
      ...prev,
      [name]:
        name === "amount" || name === "monthlyRepaymentRate" ? value : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Handle save
  const handleSave = (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      toast.error("Please fix the form errors before saving");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      ...inputs,
      amount: parseFloat(inputs.amount),
      monthlyRepaymentRate: parseFloat(inputs.monthlyRepaymentRate),
      requestTypeId: parseInt(requestTypeId),
      userId: parseInt(userId),
      isOnSaved: true,
    };

    console.log("handleSave - Sending payload:", updatedData);

    startTransition(() => {
      dispatch(updateLoanRequest(requestId, updatedData))
        .then((response) => {
          if (!response || !response.id) {
            throw new Error(
              "Invalid response from server: Update not confirmed",
            );
          }
          toast.success("Loan request saved successfully");
          navigate("/myrequests");
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to save loan request";
          console.error("Error saving loan request:", {
            error: err,
            response: err.response?.data,
          });
          toast.error(errorMessage);
          setIsSubmitting(false);
        });
    });
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      toast.error("Please fix the form errors before submitting");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      ...inputs,
      amount: parseFloat(inputs.amount),
      monthlyRepaymentRate: parseFloat(inputs.monthlyRepaymentRate),
      requestTypeId: parseInt(requestTypeId),
      userId: parseInt(userId),
      isOnSaved: false,
    };

    console.log("handleSubmit - Sending payload:", updatedData);

    startTransition(() => {
      dispatch(updateLoanRequest(requestId, updatedData))
        .then((response) => {
          if (!response || !response.id) {
            throw new Error(
              "Invalid response from server: Update not confirmed",
            );
          }
          return dispatch(submitLoanRequest(requestId, userId, requestTypeId));
        })
        .then(() => {
          toast.success("Loan request submitted successfully");
          navigate("/myrequests");
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to submit loan request";
          console.error("Error submitting loan request:", {
            error: err,
            response: err.response?.data,
          });
          toast.error(errorMessage);
          setIsSubmitting(false);
        });
    });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go back to previous page"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>Edit Loan Request</span>
          </div>
        </div>
      </div>
      <div className={styles["generic-activity-component-description"]}>
        Edit this loan request by updating the form below with the desired
        information and submitting when you are done. Ensure all fields have
        accurate information.
      </div>
      {isLoading && (
        <div className={styles["loading-message"]}>
          Loading loan request details...
        </div>
      )}
      {errors.fetchLoanRequestDetails && (
        <div className={styles["error-message"]}>
          {errors.fetchLoanRequestDetails}
        </div>
      )}
      {!isLoading && !loanDetails && !errors.fetchLoanRequestDetails && (
        <div className={styles["error-message"]}>
          No loan request details found
        </div>
      )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
          noValidate
        >
          <div className={styles["form-group"]}>
            <label className={styles["form-label"]} htmlFor="editrequestamount">
              <MonetizationOnRounded /> Amount
            </label>
            <input
              type="number"
              name="amount"
              id="editrequestamount"
              placeholder="Enter amount for request"
              required
              value={inputs.amount}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="amount-error"
              aria-invalid={!!formErrors.amount}
              min="0"
              step="0.01"
              className={styles["form-input"]}
            />
            {formErrors.amount && (
              <span className={styles["error"]} id="amount-error">
                {formErrors.amount}
              </span>
            )}
          </div>

          <div className={styles["form-group"]}>
            <label
              className={styles["form-label"]}
              htmlFor="editrequestpurpose"
            >
              <LiveHelpRounded /> Purpose
            </label>
            <textarea
              name="purpose"
              id="editrequestpurpose"
              placeholder="Enter purpose for loan"
              required
              value={inputs.purpose}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="purpose-error"
              aria-invalid={!!formErrors.purpose}
              maxLength="100"
              className={styles["form-textarea"]}
            />
            {formErrors.purpose && (
              <span className={styles["error"]} id="purpose-error">
                {formErrors.purpose}
              </span>
            )}
          </div>

          <div className={styles["form-group"]}>
            <label
              className={styles["form-label"]}
              htmlFor="editrequestrepayment"
            >
              <PercentRounded /> Repayment Percentage
            </label>
            <select
              name="monthlyRepaymentRate"
              id="editrequestrepayment"
              required
              value={inputs.monthlyRepaymentRate}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="monthlyRepaymentRate-error"
              aria-invalid={!!formErrors.monthlyRepaymentRate}
              className={styles["form-select"]}
            >
              <option value="">Select a percent</option>
              {monthlyRepaymentRates.map((rate) => (
                <option key={rate.id} value={rate.name}>
                  {rate.name}
                </option>
              ))}
            </select>
            {formErrors.monthlyRepaymentRate && (
              <span className={styles["error"]} id="monthlyRepaymentRate-error">
                {formErrors.monthlyRepaymentRate}
              </span>
            )}
          </div>

          <div className={styles["form-group"]}>
            <label
              className={styles["form-label"]}
              htmlFor="editrequestcurrency"
            >
              <PaymentsRounded /> Currency
            </label>
            <select
              name="currency"
              id="editrequestcurrency"
              required
              value={inputs.currency}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="currency-error"
              aria-invalid={!!formErrors.currency}
              className={styles["form-select"]}
            >
              <option value="">Select a currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
            {formErrors.currency && (
              <span className={styles["error"]} id="currency-error">
                {formErrors.currency}
              </span>
            )}
          </div>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={isSubmitting || isPending || isLoading || !isFormValid}
              aria-label="Save loan request"
              aria-busy={isSubmitting || isPending}
            >
              {isSubmitting || isPending ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={isSubmitting || isPending || isLoading || !isFormValid}
              aria-label="Submit loan request"
              aria-busy={isSubmitting || isPending}
            >
              {isSubmitting || isPending ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLoan;
